import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/page-header";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Section, Field, ActionBar } from "@/components/form-shell";

const EMPTY = {
  subject: "",
  venue: "Client location",
  location: "",
  meetingUrl: "",
  from: "",
  to: "",
  contactId: "",
  accountId: "",
  dealId: "",
  reminderAt: "",
  description: "",
  participants: "", // comma-separated emails/names
};
type MeetingForm = typeof EMPTY;

export default function MeetingNewPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [contacts, setContacts] = useState<{ id: string; name: string }[]>([]);
  const [accounts, setAccounts] = useState<{ id: string; name: string }[]>([]);
  const [deals, setDeals]       = useState<{ id: string; name: string }[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<MeetingForm>(EMPTY);

  useEffect(() => {
    api.get("/contacts", { params: { pageSize: 100, sortBy: "name", sortDir: "asc" } }).then((r) => setContacts(r.data.data ?? []));
    api.get("/accounts", { params: { pageSize: 100, sortBy: "name", sortDir: "asc" } }).then((r) => setAccounts(r.data.data ?? []));
    api.get("/deals",    { params: { pageSize: 100, sortBy: "name", sortDir: "asc" } }).then((r) => setDeals(r.data.data ?? []));
  }, []);

  function set<K extends keyof MeetingForm>(k: K, v: MeetingForm[K]) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function save(mode: "save" | "saveAndNew") {
    if (!form.subject || !form.from) { setError("Title and start time are required."); return; }
    setBusy(true); setError(null);
    try {
      const payload: any = {
        subject: form.subject,
        from: form.from,
      };
      if (form.venue)      payload.venue = form.venue;
      if (form.location)   payload.location = form.location;
      if (form.meetingUrl) payload.meetingUrl = form.meetingUrl;
      if (form.to)         payload.to = form.to;
      if (form.reminderAt) payload.reminderAt = form.reminderAt;
      if (form.description) payload.description = form.description;
      if (form.contactId)  payload.contactId = form.contactId;
      if (form.accountId)  payload.accountId = form.accountId;
      if (form.dealId)     payload.dealId = form.dealId;
      if (form.participants) {
        payload.participants = form.participants.split(",").map((s) => s.trim()).filter(Boolean).map((p) => {
          const isEmail = p.includes("@");
          return isEmail ? { name: p, email: p } : { name: p };
        });
      }
      await api.post("/meetings", payload);
      qc.invalidateQueries({ queryKey: ["/meetings"] });
      if (mode === "saveAndNew") setForm(EMPTY);
      else navigate("/meetings");
    } catch (err: any) {
      setError(err?.response?.data?.message ?? "Failed to create meeting.");
    } finally { setBusy(false); }
  }

  return (
    <>
      <PageHeader title="Create Meeting" description="Schedule with a contact, account, or deal." />
      <form onSubmit={(e) => { e.preventDefault(); save("save"); }} className="p-6">
        <div className="mx-auto max-w-3xl space-y-4">
          <Section title="Meeting Information">
            <Field id="subject" label="Title" required className="md:col-span-2">
              <Input id="subject" required placeholder="New Meeting" value={form.subject} onChange={(e) => set("subject", e.target.value)} />
            </Field>
            <Field id="venue" label="Meeting Venue">
              <Select id="venue" value={form.venue} onChange={(e) => set("venue", e.target.value)}>
                <option value="Client location">Client location</option>
                <option value="Our office">Our office</option>
                <option value="Zoom">Zoom</option>
                <option value="Microsoft Teams">Microsoft Teams</option>
                <option value="Google Meet">Google Meet</option>
                <option value="Phone">Phone</option>
                <option value="Other">Other</option>
              </Select>
            </Field>
            <Field id="location" label="Location">
              <Input id="location" placeholder="Address or meeting room" value={form.location} onChange={(e) => set("location", e.target.value)} />
            </Field>
            <Field id="from" label="From" required>
              <Input id="from" type="datetime-local" required value={form.from} onChange={(e) => set("from", e.target.value)} />
            </Field>
            <Field id="to" label="To">
              <Input id="to" type="datetime-local" value={form.to} onChange={(e) => set("to", e.target.value)} />
            </Field>
            <Field id="meetingUrl" label="Meeting URL" className="md:col-span-2">
              <Input id="meetingUrl" type="url" placeholder="https://teams.microsoft.com/…" value={form.meetingUrl} onChange={(e) => set("meetingUrl", e.target.value)} />
            </Field>
            <Field id="reminderAt" label="Reminder">
              <Input id="reminderAt" type="datetime-local" value={form.reminderAt} onChange={(e) => set("reminderAt", e.target.value)} />
            </Field>
            <Field id="participants" label="Participants" className="md:col-span-2">
              <Input id="participants" placeholder="name@example.com, Another Attendee" value={form.participants} onChange={(e) => set("participants", e.target.value)} />
            </Field>
          </Section>

          <Section title="Related records">
            <Field id="contactId" label="Contact">
              <Select id="contactId" value={form.contactId} onChange={(e) => set("contactId", e.target.value)}>
                <option value="">—</option>
                {contacts.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </Select>
            </Field>
            <Field id="accountId" label="Account">
              <Select id="accountId" value={form.accountId} onChange={(e) => set("accountId", e.target.value)}>
                <option value="">—</option>
                {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </Select>
            </Field>
            <Field id="dealId" label="Deal">
              <Select id="dealId" value={form.dealId} onChange={(e) => set("dealId", e.target.value)}>
                <option value="">—</option>
                {deals.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </Select>
            </Field>
          </Section>

          <Section title="Description">
            <div className="md:col-span-2">
              <textarea
                className="flex min-h-[100px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                placeholder="Agenda, notes, expected outcome…"
                value={form.description}
                onChange={(e) => set("description", e.target.value)}
              />
            </div>
          </Section>

          <ActionBar
            busy={busy}
            canSubmit={!!form.subject && !!form.from}
            onCancel={() => navigate("/meetings")}
            onSaveAndNew={() => save("saveAndNew")}
            onSubmit={() => save("save")}
            error={error}
          />
        </div>
      </form>
    </>
  );
}
